/**
 * The three lock writes: acquire, extend, release.
 *
 * Contention is not an HTTP error here. Acquire and extend always answer `200`
 * and report the outcome in `acquired`, so the boolean in the body is the only
 * signal that decides whether this user may edit — never the status code, and
 * never our own reading of `editing_user`.
 *
 * Acquire and extend echo the live `edit_lock`, so each writes its normalized
 * snapshot straight into the meta query cache. That keeps one source of truth
 * for lock state regardless of whether it came from a poll or a write, and on a
 * lost race it immediately names the real holder.
 */
import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { tpoGenUrls } from 'config/urls';
import { sendNotification } from 'utils/helperMethods';

import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import { EMPTY_SNAPSHOT } from '../../editLockAdapter';
import { commitEditLockSnapshot, postLockAction, postRelease } from '../../editLockActions';
import type { EditLockSnapshot } from '../../types';

export function useEditLockMutations(planId: string | undefined) {
    const queryClient = useQueryClient();

    const cacheSnapshot = useCallback(
        (snapshot: EditLockSnapshot) => (planId ? commitEditLockSnapshot(queryClient, planId, snapshot) : undefined),
        [queryClient, planId],
    );

    const { mutateAsync: acquireAsync, isPending: isAcquiring } = useMutation({
        mutationFn: () => postLockAction(tpoGenUrls.acquirePlanEditLock, planId as string),
        onSuccess: ({ snapshot }) => cacheSnapshot(snapshot),
    });

    const { mutateAsync: extendAsync } = useMutation({
        mutationFn: () => postLockAction(tpoGenUrls.extendPlanEditLock, planId as string),
        onSuccess: ({ snapshot }) => cacheSnapshot(snapshot),
    });

    const { mutateAsync: releaseAsync, isPending: isReleasing } = useMutation({
        mutationFn: () => postRelease(planId as string),
        onSuccess: (released) => {
            // Release answers with no `edit_lock`, so the only state we can
            // assert is the one we caused. A refused release means someone else
            // holds it and we do not know who; re-read rather than guess.
            if (released) return cacheSnapshot(EMPTY_SNAPSHOT);
            if (planId) return queryClient.invalidateQueries({ queryKey: tpoGenKeys.editLock(planId) });
        },
    });

    /** Resolves the server's `acquired` verdict. */
    const acquire = useCallback(async (): Promise<boolean> => {
        if (!planId) return false;
        try {
            const { acquired } = await acquireAsync();
            return acquired;
        } catch {
            sendNotification('error', 'Could not start editing. Please try again.', 5, 450);
            return false;
        }
    }, [acquireAsync, planId]);

    /**
     * Renews the lease. Extend never resurrects an expired lock, so a false
     * verdict — like a rejected request — means the session is over and the
     * caller must drop to read-only rather than assume it lives.
     */
    const extend = useCallback(async (): Promise<boolean> => {
        if (!planId) return false;
        try {
            const { acquired } = await extendAsync();
            return acquired;
        } catch {
            return false;
        }
    }, [extendAsync, planId]);

    /** Resolves true when the lock was actually given up. */
    const release = useCallback(async (): Promise<boolean> => {
        if (!planId) return false;
        try {
            return await releaseAsync();
        } catch {
            // The lease expires on its own, so a failed release is not fatal.
            sendNotification('error', 'Could not exit edit mode cleanly. Your session will expire on its own.', 5, 450);
            return false;
        }
    }, [releaseAsync, planId]);

    return { acquire, extend, release, isAcquiring, isReleasing };
}
